<!--
  The bell: how many lines of the caller's feed are unread, asked on an interval, and the newest
  lines when it is opened — each in plain text, opening its screen and marking itself read; « Tout
  marquer comme lu »; and the way to the settings where what reaches the feed is chosen. `rail` is
  the navigation rail's entry, `icon` the user drawer's compact button; both read the same cache.
-->
<template>
  <v-menu
    v-model="open"
    :location="variant === 'rail' ? 'end bottom' : 'bottom end'"
    :close-on-content-click="false"
    :offset="variant === 'rail' ? 12 : 4"
    max-width="400"
  >
    <template #activator="{ props: menuProps }">
      <div
        v-if="variant === 'rail'"
        v-bind="menuProps"
        class="notification-bell"
        role="button"
        tabindex="0"
        :aria-label="label"
        @keydown.enter="open = !open"
      >
        <div class="notification-bell__button" :class="{ 'notification-bell__button--active': open }">
          <v-badge :content="badge ?? ''" :model-value="badge !== null" color="error" offset-x="-2" offset-y="-2">
            <v-icon
              :icon="badge !== null ? mdiBellBadgeOutline : mdiBellOutline"
              size="22"
              class="notification-bell__icon"
            />
          </v-badge>
        </div>
        <div class="notification-bell__title">{{ t('notifications.bell.title') }}</div>
      </div>
      <v-btn v-else v-bind="menuProps" icon variant="text" size="small" :aria-label="label">
        <v-badge :content="badge ?? ''" :model-value="badge !== null" color="error">
          <v-icon :icon="mdiBellOutline" size="18" />
        </v-badge>
      </v-btn>
    </template>

    <v-card class="notification-bell__panel">
      <div class="notification-bell__header">
        <span class="notification-bell__heading">{{ t('notifications.bell.title') }}</span>
        <v-btn
          variant="text"
          size="small"
          color="primary"
          :disabled="!unread || markAll.isPending.value"
          :loading="markAll.isPending.value"
          @click="readAll"
        >
          {{ t('notifications.bell.markAllRead') }}
        </v-btn>
      </div>
      <v-divider />

      <div v-if="feed.isPending.value" class="notification-bell__state">
        <v-progress-circular indeterminate size="24" width="2" color="primary" />
      </div>
      <div v-else-if="feed.isError.value" class="notification-bell__state">
        {{ t('notifications.bell.loadFailed') }}
      </div>
      <div v-else-if="entries.length === 0" class="notification-bell__empty">
        <v-icon :icon="mdiBellSleepOutline" size="32" class="notification-bell__empty-icon" />
        <div class="notification-bell__empty-title">{{ t('notifications.bell.empty.title') }}</div>
        <p class="notification-bell__empty-text">{{ t('notifications.bell.empty.description') }}</p>
      </div>
      <v-list v-else density="compact" class="notification-bell__list" nav>
        <v-list-item
          v-for="entry in entries"
          :key="entry.uid"
          :value="entry.uid"
          class="notification-bell__line"
          :class="{ 'notification-bell__line--unread': isUnread(entry) }"
          :to="entry.link"
          :active="false"
          @click="openLine(entry)"
        >
          <template #prepend>
            <v-icon :icon="iconOf(entry)" size="20" class="notification-bell__line-icon" />
          </template>
          <div class="notification-bell__line-text">{{ lineText(entry) }}</div>
          <div class="notification-bell__line-when">{{ formatRelativeIntl(entry.created_at, locale) }}</div>
          <template #append>
            <span
              v-if="isUnread(entry)"
              class="notification-bell__dot"
              :aria-label="t('notifications.bell.unreadMarker')"
              role="img"
            />
          </template>
        </v-list-item>
      </v-list>

      <v-divider />
      <div class="notification-bell__footer">
        <v-btn variant="text" size="small" color="primary" :to="{ name: 'settings:notifications' }" @click="leave">
          {{ t('notifications.bell.manage') }}
        </v-btn>
      </div>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useRoute } from 'vue-router'
  import {
    mdiAccountPlusOutline,
    mdiAccountClockOutline,
    mdiBellBadgeOutline,
    mdiBellOutline,
    mdiBellSleepOutline,
    mdiCashRegister,
    mdiEmailOutline,
    mdiInboxArrowDown,
    mdiFileClockOutline,
    mdiFileUploadOutline,
  } from '@mdi/js'
  import type { NotificationEntry } from '@/api/notifications/feed'
  import { useToast } from '@/apps/core/composables/useToast'
  import { formatRelativeIntl } from '@/apps/core/utils/formatRelativeIntl'
  import { badgeText, isUnread, lineCopy } from '@/apps/notifications/feed'
  import { kindCopy } from '@/apps/notifications/preferences'
  import {
    useMarkAllNotificationsRead,
    useMarkNotificationRead,
    useNotificationFeed,
    useUnreadNotificationCount,
  } from '@/apps/notifications/queries'

  withDefaults(defineProps<{ variant?: 'rail' | 'icon' }>(), { variant: 'rail' })

  const emit = defineEmits<{
    /** A line or the settings were opened: whoever holds the bell may close what it sits in. */
    navigate: []
  }>()

  const { t, locale } = useI18n()
  const route = useRoute()
  const toast = useToast()

  const open = ref(false)
  const inOrganization = computed(() => typeof route.params.organizationId === 'string')

  const unreadCount = useUnreadNotificationCount({ enabled: inOrganization })
  const feed = useNotificationFeed({ enabled: () => open.value && inOrganization.value })
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()

  // Opening the bell reads the list, and the count with it, so the badge never disagrees with the
  // lines just read: a line whose subject left between two polls leaves both at once.
  watch(open, (isOpen) => {
    if (isOpen && inOrganization.value) void unreadCount.refetch()
  })

  const unread = computed(() => unreadCount.data.value ?? 0)
  const badge = computed(() => badgeText(unread.value))
  const entries = computed(() => feed.data.value?.data ?? [])
  const label = computed(() =>
    unread.value > 0
      ? `${t('notifications.bell.open')} — ${t('notifications.bell.unread', { count: unread.value }, unread.value)}`
      : t('notifications.bell.open'),
  )

  /** What a line says, in plain text — interpolated by Vue, never rendered as markup. */
  function lineText(entry: NotificationEntry): string {
    const copy = lineCopy(entry, locale.value)
    const key = `notifications.lines.${copy.key}`
    return copy.plural === undefined ? t(key, copy.params) : t(key, copy.params, copy.plural)
  }

  function iconOf(entry: NotificationEntry): string {
    switch (kindCopy(entry.kind)) {
      case 'uploadReceived':
        return mdiFileUploadOutline
      case 'uploadPendingModeration':
        return mdiFileClockOutline
      case 'membershipRequestReceived':
        return mdiAccountPlusOutline
      case 'membershipRequestsWaiting':
        return mdiAccountClockOutline
      case 'saleReceived':
        return mdiCashRegister
      case 'contactMessageReceived':
        return mdiEmailOutline
      case 'depositReceived':
        return mdiInboxArrowDown
      default:
        return mdiBellOutline
    }
  }

  /** A line was opened — its link navigates — so mark it read on the way, and close the bell. */
  function openLine(entry: NotificationEntry): void {
    if (isUnread(entry)) {
      markRead.mutate(entry.uid, { onError: () => toast.error(t('notifications.bell.markFailed')) })
    }
    leave()
  }

  function readAll(): void {
    markAll.mutate(undefined, { onError: () => toast.error(t('notifications.bell.markFailed')) })
  }

  function leave(): void {
    open.value = false
    emit('navigate')
  }
</script>

<style lang="scss" scoped>
  @use '@/styles/design-tokens' as *;

  // The rail's entry, drawn as the rail's other bottom entries are.
  .notification-bell {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    cursor: pointer;
    margin-bottom: 1rem;

    &:hover {
      .notification-bell__button:not(.notification-bell__button--active) .notification-bell__icon {
        color: rgba(var(--v-theme-on-surface), 0.87);
      }

      .notification-bell__title {
        color: rgba(var(--v-theme-on-surface), 0.95);
      }
    }

    &__button {
      transition: all 0.2s ease;
      width: 42px;
      height: 42px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(var(--v-theme-surface), 1);

      &--active {
        background-color: rgba(var(--v-theme-primary), 0.08);

        .notification-bell__icon {
          color: rgb(var(--v-theme-primary));
        }
      }
    }

    &__icon {
      color: rgba(var(--v-theme-on-surface), 0.75);
      transition: color 0.2s ease;
    }

    &__title {
      letter-spacing: 0.02em;
      font-size: 0.625rem;
      color: rgba(var(--v-theme-on-surface), 0.75);
      margin-top: 0.25rem;
      font-weight: 500;
      transition: color 0.2s ease;
    }

    &__panel {
      width: 380px;
      max-width: calc(100vw - 32px);
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.5rem 0.5rem 0.5rem 1rem;
    }

    &__heading {
      font-weight: 600;
    }

    &__state {
      display: flex;
      justify-content: center;
      padding: 1.5rem 1rem;
      color: rgba(var(--v-theme-on-surface), 0.7);
    }

    &__empty {
      padding: 1.5rem 1.25rem;
      text-align: center;
    }

    &__empty-icon {
      color: rgba(var(--v-theme-on-surface), 0.4);
    }

    &__empty-title {
      margin-top: 0.5rem;
      font-weight: 600;
    }

    &__empty-text {
      margin-top: 0.25rem;
      font-size: 0.8125rem;
      color: rgba(var(--v-theme-on-surface), 0.7);
    }

    &__list {
      max-height: 420px;
      overflow-y: auto;
    }

    &__line {
      align-items: flex-start;

      &--unread .notification-bell__line-text {
        font-weight: 600;
      }
    }

    &__line-icon {
      color: rgba(var(--v-theme-on-surface), 0.6);
    }

    &__line-text {
      font-size: 0.8125rem;
      line-height: 1.35;
      white-space: normal;
      overflow-wrap: anywhere;
    }

    &__line-when {
      margin-top: 0.125rem;
      font-size: 0.75rem;
      color: rgba(var(--v-theme-on-surface), 0.6);
    }

    &__dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: rgb(var(--v-theme-primary));
    }

    &__footer {
      display: flex;
      justify-content: center;
      padding: 0.25rem;
    }
  }
</style>
